/* eslint-disable react/no-array-index-key */
import React from "react";
import { DescriptionList, DescriptionListItem, DescriptionListSection } from "buba-components-ts";

/**
 * Generates a DescriptionList component from a configuration object
 * @param {Object} config - Configuration object
 * @param {Array} config.sections - Array of section configurations or arrays of sub-sections
 * @param {string} config.sections[].title - Section title
 * @param {Array} config.sections[].items - Array of items in the section
 * @param {string} config.sections[].items[].label - Item label
 * @param {any} config.sections[].items[].data - Item data to display
 * @param {boolean} [config.sections[].items[].fullWidth] - Whether item should be full width
 * @param {string} [customStyles] - Custom styles to apply to DescriptionList
 * @returns {JSX.Element} DescriptionList component
 */
export const generateDescriptionList = (config, customStyles) => {
  const { sections } = config;

  // Process sections to handle both single sections and arrays of sub-sections
  const processedSections = sections
    .map((sectionOrArray, mainIndex) => {
      // Check if this is an array of sub-sections
      if (Array.isArray(sectionOrArray)) {
        // Filter sub-sections that have at least one item with truthy data
        const validSubSections = sectionOrArray.filter(subSection => {
          return subSection.items && subSection.items.some(item => item.data);
        });

        if (validSubSections.length === 0) {
          return null;
        }

        // Create a stable key for the main section group
        const mainSectionKey = `main-group-${validSubSections.map(s => s.title).join("-")}-${mainIndex}`;

        // Return a main section wrapper containing sub-sections
        return (
          <div key={mainSectionKey} style={{ marginBottom: "6rem" }}>
            {validSubSections.map((subSection, subIndex) => {
              // Filter items with truthy data
              const validItems = subSection.items.filter(item => item.data);

              if (validItems.length === 0) {
                return null;
              }

              return (
                <DescriptionListSection
                  key={`${mainSectionKey}-${subSection.title}-${subIndex}`}
                  title={subSection.title}
                >
                  {validItems.map((item, itemIndex) => (
                    <DescriptionListItem
                      key={`${mainSectionKey}-${subSection.title}-${item.label}-${itemIndex}`}
                      label={item.label}
                      fullWidth={item.fullWidth}
                    >
                      {item.data}
                    </DescriptionListItem>
                  ))}
                </DescriptionListSection>
              );
            })}
          </div>
        );
      }

      // Handle single section
      const section = sectionOrArray;

      // Filter items with truthy data
      const validItems = section.items ? section.items.filter(item => item.data) : [];

      if (validItems.length === 0) {
        return null;
      }

      const singleSectionKey = `single-${section.title}-${mainIndex}`;

      return (
        <div key={singleSectionKey} style={{ marginBottom: "2rem" }}>
          <DescriptionListSection title={section.title}>
            {validItems.map((item, itemIndex) => (
              <DescriptionListItem
                key={`${section.title}-${item.label}-${itemIndex}`}
                label={item.label}
                fullWidth={item.fullWidth}
              >
                {item.data}
              </DescriptionListItem>
            ))}
          </DescriptionListSection>
        </div>
      );
    })
    .filter(Boolean); // Remove null sections

  if (processedSections.length === 0) {
    return null;
  }

  return <DescriptionList customStyles={customStyles}>{processedSections}</DescriptionList>;
};

export default generateDescriptionList;
